import * as Sentry from '@sentry/nextjs'
import _isObject from 'lodash/isObject'
import _isString from 'lodash/isString'
import _size from 'lodash/size'

import { getErrorMessage } from './message'
import {
  SENTRY_DEVELOPMENT,
  SENTRY_PRODUCTION,
  SENTRY_SANDBOX,
  shouldReportErrorToSentry,
  type SentryEnvironment,
} from './sentry.shared'
import {
  applyAxiosErrorContext,
  buildAdditionalInfoContext,
  findAxiosError,
  findErrorInArgs,
} from './sentry.utils'

export { SENTRY_DEVELOPMENT, SENTRY_PRODUCTION, SENTRY_SANDBOX, type SentryEnvironment }

export class SentryConfig {
  private static instance: SentryConfig | null = null

  private constructor() {
    // No initialization needed - handled by separate config files
  }

  static getInstance(): SentryConfig {
    if (!SentryConfig.instance) {
      SentryConfig.instance = new SentryConfig()
    }
    return SentryConfig.instance
  }

  public capture(error: Error | string, ...args: unknown[]): void {
    this.captureWithScope({}, error, ...args)
  }

  public captureWithScope(
    {
      tags,
    }: {
      tags?: Record<string, string>
    },
    error: Error | string,
    ...args: unknown[]
  ): void {
    if (!shouldReportErrorToSentry(error, args)) {
      return
    }

    try {
      Sentry.withScope(scope => {
        if (tags) {
          Object.entries(tags).forEach(([key, value]) => {
            scope.setTag(key, value)
          })
        }

        if (_size(args) > 0) {
          const additionalInfo = buildAdditionalInfoContext(args)
          scope.setContext('additionalInfo', additionalInfo)
          scope.setExtra('additionalArgsJson', additionalInfo.argsJson)
        }

        const axiosError = findAxiosError(error, args)
        if (axiosError) {
          applyAxiosErrorContext(scope, axiosError)
        }

        if (_isString(error) && findErrorInArgs(args)) {
          scope.setExtra('captureMessage', error)
          scope.setTag('logPrefix', error)
        }

        this.captureInternal(error, args)
      })
    } catch {
      // Silent fail - don't break the app if Sentry fails
    }
  }

  public async flush(timeoutMs = 3000): Promise<boolean> {
    try {
      return await Sentry.flush(timeoutMs)
    } catch {
      return false
    }
  }

  private captureInternal(error: unknown, args: unknown[] = []): void {
    const resolvedError = error instanceof Error ? error : findErrorInArgs(args)

    if (resolvedError) {
      const errorMessage = getErrorMessage(resolvedError)
      const extra: Record<string, unknown> = { message: errorMessage }

      if (!resolvedError.stack) {
        extra.captureSiteStack = new Error('SentryConfig.capture').stack
      }

      const exceptionToReport =
        resolvedError.stack || resolvedError.name === 'Error'
          ? resolvedError
          : Object.assign(new Error(`${resolvedError.name}: ${errorMessage}`), {
              cause: resolvedError,
            })

      Sentry.captureException(exceptionToReport, { extra })
      return
    }

    if (typeof error === 'string') {
      Sentry.captureMessage(error, 'error')
      return
    }

    const errorMessage = _isObject(error) ? JSON.stringify(error) : String(error)
    Sentry.captureMessage(errorMessage, 'error')
  }

  public setUser(user: { id?: string; email?: string; username?: string }): void {
    Sentry.setUser(user)
  }

  public clearUser(): void {
    Sentry.setUser(null)
  }

  public get isEnabled(): boolean {
    return !!process.env.NEXT_PUBLIC_SENTRY_DSN
  }

  public get currentEnvironment(): SentryEnvironment {
    return process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT as SentryEnvironment
  }

  public get currentDsn(): string {
    return process.env.NEXT_PUBLIC_SENTRY_DSN || ''
  }
}

export const sentryConfig = SentryConfig.getInstance()
